import { lazy } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { Redirect } from './Redirect';

import { ProtectedRoute, Layout, ExternalBaseComponent } from 'components';
import { Paths, Roles } from 'enums';
import { useAppSelector } from 'hooks';
import { selectUserRole } from 'state';

const Login = lazy(() => import('pages/Login'));
const RestorePassword = lazy(() => import('pages/RestorePassword'));
const UsersManagement = lazy(() => import('pages/UsersManagement'));
const Communities = lazy(() => import('pages/Communities'));
const Companies = lazy(() => import('pages/Companies'));
const NewCompany = lazy(() => import('pages/Companies/NewCompany'));
const CompanyInfo = lazy(() => import('pages/Companies/CompanyInfo'));
const Properties = lazy(() => import('pages/Properties'));
const Management = lazy(() => import('pages/Management'));
const Inspection = lazy(() => import('pages/Inspection'));
const InspectionDetails = lazy(() => import('pages/Inspection/InspectionDetails'));
const InspectionMissingProperties = lazy(
  () => import('pages/Inspection/InspectionMissingProperties'),
);
const Inspections = lazy(() => import('pages/Inspections'));
const SignUp = lazy(() => import('pages/SignUp'));
const ConfirmationCode = lazy(() => import('pages/RestorePassword/ConfirmationCode'));
const NewPassword = lazy(() => import('pages/RestorePassword/NewPassword'));
const AddInspection = lazy(() => import('pages/AddInspection'));
const PropertyPage = lazy(() => import('pages/Management/PropertyPage'));
const CommunityInfo = lazy(() => import('pages/Communities/CommunityInfo'));
const NewCommunity = lazy(() => import('pages/Communities/NewCommunity'));
const UploadCsv = lazy(() => import('pages/Management/UploadCsv'));

const ManagementRoutes = () => {
  return (
    <Routes>
      <Route index element={<Management />} />
      <Route path={Paths.PROPERTY} element={<PropertyPage />} />
      <Route path={`${Paths.PROPERTY}/:id`} element={<PropertyPage />} />
      <Route path={Paths.UPLOAD_CSV} element={<UploadCsv />} />
    </Routes>
  );
};

const CommunitiesRoutes = () => {
  return (
    <Routes>
      <Route index element={<Communities />} />
      <Route path={Paths.NEW_COMMUNITY} element={<NewCommunity />} />
      <Route path="/:id" element={<CommunityInfo />} />
    </Routes>
  );
};

const CompaniesRoutes = () => {
  return (
    <Routes>
      <Route index element={<Companies />} />
      <Route path={Paths.NEW_COMPANY} element={<NewCompany />} />
      <Route path="/:id" element={<CompanyInfo />} />
    </Routes>
  );
};

const InspectionsRoutes = () => {
  return (
    <Routes>
      <Route index element={<Inspections />} />
      <Route path="/:id" element={<Inspection />} />
      <Route path="/:id/details" element={<InspectionDetails />} />
      <Route path="/:id/missing-properties" element={<InspectionMissingProperties />} />
    </Routes>
  );
};

const InternalRoutes = () => {
  const role = useAppSelector(selectUserRole);
  let defaultPath = Paths.COMMUNITIES;

  switch (role) {
    case Roles.SUPER_ADMIN:
      defaultPath = Paths.COMPANIES;
      break;
    case Roles.PROPERTY_CUSTOMER:
      defaultPath = Paths.PROPERTIES;
      break;
    default:
      break;
  }

  return (
    <Routes>
      <Route path="*" element={<Navigate to={defaultPath} />} />
      <Route path={Paths.USERS_MANAGEMENT} element={<UsersManagement />} />
      <Route path={`${Paths.COMMUNITIES}/*`} element={<CommunitiesRoutes />} />
      <Route path={`${Paths.COMPANIES}/*`} element={<CompaniesRoutes />} />
      <Route path={Paths.PROPERTIES} element={<Properties />} />
      <Route path={`${Paths.MANAGEMENT}/*`} element={<ManagementRoutes />} />
      <Route path="/inspection/:id" element={<Redirect to="/inspections" />} />
      <Route path={`${Paths.INSPECTIONS}/*`} element={<InspectionsRoutes />} />
      <Route path={`${Paths.ADD_INSPECTION}/:id`} element={<AddInspection />} />
      <Route path={`${Paths.ADD_INSPECTION}`} element={<AddInspection />} />
    </Routes>
  );
};

const RestorePasswordRoutes = () => {
  return (
    <Routes>
      <Route index element={<RestorePassword />} />
      <Route path={Paths.CODE} element={<ConfirmationCode />} />
      <Route path={Paths.NEW_PASSWORD} element={<NewPassword />} />
    </Routes>
  );
};

export const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path={Paths.LOGIN}
        element={
          <ExternalBaseComponent>
            <Login />
          </ExternalBaseComponent>
        }
      />
      <Route
        path={`${Paths.RESTORE_PASSWORD}/*`}
        element={
          <ExternalBaseComponent>
            <RestorePasswordRoutes />
          </ExternalBaseComponent>
        }
      />
      <Route
        path={Paths.SIGN_UP}
        element={
          <ExternalBaseComponent>
            <SignUp />
          </ExternalBaseComponent>
        }
      />

      <Route
        path="*"
        element={
          <Layout>
            <ProtectedRoute>
              <InternalRoutes />
            </ProtectedRoute>
          </Layout>
        }
      />
    </Routes>
  );
};
